import React from 'react';
import { Card, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { INVOICE_DESCRIPTION_ALLOWED_CHARACTERS, handleInvoicingDescriptionChange } from '../../common/invoicingUtils';
import './other-details.scss';

interface OtherDetailsProps {
  otherDetails: {
    customerNotes: string;
    supportingFiles: Array<{
      fileName: string;
      fileContent: string;
      file: File;
    }>;
    communicationEmails: string[];
  };
  newEmail: string;
  onNewEmailChange: (email: string) => void;
  onAddEmail: () => void;
  onRemoveEmail: (email: string) => void;
  onCustomerNotesChange: (notes: string) => void;  // NEW - Handler for customer notes change
  onSupportingFileChange?: (file: File | null) => void;  // NEW - Handler for file upload
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;
}

const OtherDetails: React.FC<OtherDetailsProps> = ({
  otherDetails,
  newEmail,
  onNewEmailChange,
  onAddEmail,
  onRemoveEmail,
  onCustomerNotesChange,
  onSupportingFileChange,
  copiedInvoiceData,
  isEditModeInvoice
}) => {
  const { t } = useTranslation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string>('');

  // Check for query parameters
  const searchParams = new URLSearchParams(window.location.search);
  const hasQueryParams = searchParams.has('edit') || 
                        searchParams.has('preId') || 
                        searchParams.has('invoiceNumber') || 
                        searchParams.has('timesheet') || 
                        searchParams.has('createdDate');

  
  // Use react-hook-form for validation
  const {
    register,
    setValue,
    trigger,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      description: hasQueryParams ? (otherDetails.customerNotes || '') : ''
    }
  });
 // Reset form when navigating to fresh invoice page (no query parameters)
  React.useEffect(() => {
    // If no relevant query parameters, reset all fields
    if (!hasQueryParams) {
      setValue('description', '');
      setSelectedFile(null);
      setFileError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Notify parent component
      if (onCustomerNotesChange) {
        onCustomerNotesChange('');
      }
      if (onSupportingFileChange) {
        onSupportingFileChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]); // Run whenever URL changes
  
  // Load saved file from copiedInvoiceData or edit mode
  React.useEffect(() => {
    // Only load file if query params exist
    if (!hasQueryParams) {
      return;
    }
    // Check if copiedInvoiceData has file array
    if (copiedInvoiceData?.file && copiedInvoiceData.file.length > 0) {
      const fileData = copiedInvoiceData.file[0];
      
     // Check if fileData has fileName and fileContent
      if (fileData.fileName && fileData.fileContent) {
        try {
          // Decode base64 fileContent to create File object
          const binaryString = window.atob(fileData.fileContent);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          
          // Create Blob and File object
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const file = new File([blob], fileData.fileName, { type: 'application/pdf' });
          
          setSelectedFile(file);
          if (onSupportingFileChange) {
            onSupportingFileChange(file);
          }
         } catch (error) {
          console.error('❌ [OtherDetails] Error converting base64 to file:', error);
        }
      } else if (fileData instanceof File) {
        // If it's already a File object
        setSelectedFile(fileData);
         }
    } 
    // Fallback to old structure
    else if (copiedInvoiceData?.supportingFile || (isEditModeInvoice && otherDetails.supportingFiles?.[0])) {
      const existingFile = copiedInvoiceData?.supportingFile || otherDetails.supportingFiles?.[0];
      if (existingFile) {
        const fileToSet = existingFile.file || existingFile;
        setSelectedFile(fileToSet);
       }
    }
  }, [copiedInvoiceData, isEditModeInvoice, otherDetails.supportingFiles, onSupportingFileChange]);
  
  // Watch the description value to display it in the textarea
  const descriptionValue = watch('description'); 
  // Track if data has been populated to prevent re-render
  const isDataPopulatedRef = React.useRef(false);
  
  // Populate data from copiedInvoiceData when it changes
  React.useEffect(() => {
    // Only populate if query params exist
    if (!hasQueryParams) {
      return;
    }
    if (copiedInvoiceData && !isDataPopulatedRef.current) {
    
      // Populate customer notes from description field
      if (copiedInvoiceData.description) {
        setValue('description', copiedInvoiceData.description);
        if (onCustomerNotesChange) {
          onCustomerNotesChange(copiedInvoiceData.description);
        }
        }
      
      // Mark as populated
      isDataPopulatedRef.current = true;
    }
 }, [copiedInvoiceData, onCustomerNotesChange, setValue]);
  
  // Reset the ref when copiedInvoiceData changes to a new object
  React.useEffect(() => {
    if (copiedInvoiceData) {
      isDataPopulatedRef.current = false;
    }
  }, [copiedInvoiceData?.invoiceId, copiedInvoiceData?.id]);
  
  // Sync otherDetails.customerNotes with form value
  React.useEffect(() => {
    // Only sync if query params exist AND otherDetails has a value
    if (hasQueryParams && otherDetails.customerNotes !== undefined && otherDetails.customerNotes !== '') {
      setValue('description', otherDetails.customerNotes);
    }
 }, [otherDetails.customerNotes, setValue, hasQueryParams]);
  
  // Handle description change with validation
  const handleDescriptionChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    await handleInvoicingDescriptionChange(e, trigger, setValue);
    const newValue = e.target.value;
    if (onCustomerNotesChange) {
      onCustomerNotesChange(newValue);
    }
  };
  
  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');
    
    if (file) {
      // Validate file type (PDF only)
      if (file.type !== 'application/pdf') {
        setFileError(t('FILE_UPLOAD_PDF_ONLY'));
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        setFileError(t('FILE_UPLOAD_MAX_SIZE'));
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      // File is valid
      setSelectedFile(file);
      if (onSupportingFileChange) {
        onSupportingFileChange(file);
      }
    }
  };
  
  // Handle browse button click
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };
  
  // Handle file removal
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onSupportingFileChange) {
      onSupportingFileChange(null);
    }
  };

  return (
    <Card className="invoice-card other-details mb-4">
      <Card.Header className="card-header">
        <span className="od-head-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13h6M9 17h4" />
          </svg>
        </span>
        <Card.Title as="h3">{t('OTHER_DETAILS')}</Card.Title>
      </Card.Header>
      <Card.Body className="card-body">
        <div className="other-details-content">
          {/* Customer Notes */}
          <div className="customer-notes">
            <label className="input-label">{t('DESCRIPTION')}</label>
            <div className="textarea-box">
              <textarea 
                className="form-control m-0"
                value={descriptionValue || ''}
                {...register("description", {
                  required: t("Enter at least 20 characters or 3 words"),
                  validate: (value) => {
                    const length = value?.length || 0;
                    const wordCount = value?.trim().split(/\s+/).filter((word: string) => word.length > 0).length || 0;
                    
                    if (length > 0 && length < 20 && wordCount < 3) {
                      return t("Enter at least 20 characters or 3 words");
                    }
                    
                    if (length > 1024) {
                      return t("Maximum character limit 1024");
                    }
                    
                    const invalidCharactersInDescription = value
                      ?.split("")
                      ?.filter((char: string) => !INVOICE_DESCRIPTION_ALLOWED_CHARACTERS.test(char));

                    if (invalidCharactersInDescription?.length > 0) {
                      return `${t("Invalid characters detected:")} ${invalidCharactersInDescription.join(", ")}`;
                    }
                    return true;
                  },
                })}
                onChange={handleDescriptionChange}
                placeholder={t('THANKS_FOR_BUSINESS')}
              />
            </div>
             <div style={{ color: "var(--text-icons-light-base-second, #68727D)",
fontFamily: "Plus Jakarta Sans",
fontSize: "14px",
fontStyle: "normal",
fontWeight: 400,
lineHeight: "20px" /* 142.857% */}}>
               {t('THIS_WILL_DISPLAY_ON_INVOICE')}
              </div>
            {errors?.description?.message && (
              <div className="alert alert-danger mt-3" role="alert">
                {errors.description.message.toString()}
              </div>
            )}
          </div>
          
          {/* Supporting Files */}
          <div className="supporting-files">
            <label className="input-label">{t('ADD_SUPPORTING_FILE')}</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <div className="file-input" onClick={handleBrowseClick} style={{ cursor: 'pointer' }}>
              <span className="file-browse-text">{t('BROWSE_FILES')}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M6.66667 5.83333V4.16667C6.66667 3.50362 6.93006 2.86774 7.39933 2.39832C7.86869 1.92899 8.50454 1.66667 9.16667 1.66667H10.8333C11.4964 1.66667 12.1323 1.92899 12.6017 2.39832C13.0709 2.86774 13.3333 3.50362 13.3333 4.16667V5.83333M6.66667 5.83333H4.16667C3.50362 5.83333 2.86774 6.09673 2.39832 6.566C1.92899 7.03536 1.66667 7.67121 1.66667 8.33333V15C1.66667 15.6631 1.92899 16.2989 2.39832 16.7683C2.86774 17.2376 3.50362 17.5 4.16667 17.5H15.8333C16.4964 17.5 17.1323 17.2376 17.6017 16.7683C18.071 16.2989 18.3333 15.6631 18.3333 15V8.33333C18.3333 7.67121 18.071 7.03536 17.6017 6.566C17.1323 6.09673 16.4964 5.83333 15.8333 5.83333H13.3333M6.66667 5.83333H13.3333" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            {/* Selected file display */}
            {selectedFile && (
              <div className="selected-file" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: '1 1 auto' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M9.33333 1.33333H4C3.64638 1.33333 3.30724 1.47381 3.05719 1.72386C2.80714 1.97391 2.66667 2.31304 2.66667 2.66667V13.3333C2.66667 13.687 2.80714 14.0261 3.05719 14.2761C3.30724 14.5262 3.64638 14.6667 4 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2761C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33333L9.33333 1.33333Z" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9.33333 1.33333V5.33333H13.3333" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span
                    title={selectedFile.name}
                    style={{ fontSize: '14px', color: '#1e40af', minWidth: 0, flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >{selectedFile.name}</span>
                  <span style={{ fontSize: '12px', color: '#64748b', flexShrink: 0, whiteSpace: 'nowrap' }}>({(selectedFile.size / 1024).toFixed(2)} KB)</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', flexShrink: 0 }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            
            {/* Error message */}
            {fileError && (
              <div className="alert alert-danger mt-2" role="alert" style={{ color: '#dc2626', fontSize: '14px', marginTop: '8px', padding: '8px 12px', backgroundColor: '#fee2e2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                {fileError}
              </div>
            )}
          </div>
          
          {/* Communication Emails */}
          <div className="communication-emails">
            {/* <label className="input-label">{t('COMMUNICATION_EMAILS')}</label>
            <div className="email-input-container">
              <div className="email-input">
                <svg className="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <input 
                  type="email" 
                  value={newEmail}
                  onChange={(e) => onNewEmailChange(e.target.value)}
                  placeholder={t('ENTER_EMAIL_ID')}
                />
              </div>
              <Button className="add-email-btn" onClick={onAddEmail}>
                {t('ADD')}
              </Button>
            </div> */}
            
            {/* Email Tags */}
            {/* {otherDetails.communicationEmails.length > 0 && (
              <div className="email-tags">
                {otherDetails.communicationEmails.map((email, index) => (
                  <div key={index} className="email-tag">
                    <span>{email}</span>
                    <button 
                      className="remove-email-btn"
                      onClick={() => onRemoveEmail(email)}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )} */}
          </div>
       
        </div>
      </Card.Body>
    </Card>
  );
};

export default OtherDetails;